'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { UspsCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/UspsCardFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';

import { useIntl } from 'react-intl';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { uspsMessages } from './UspsCard.client.messages';
import { AdditionalInfoItem } from '../AdditionalInfoItem/AdditionalInfoItem.client';
import { getValidInfoList } from '../getValidInfoList/getValidInfoList';

/* GraphQL */ `
  fragment UspsCardFragment on UspsInfo {
    infoList {
      serviceLayerId
      translationKey
      iconUrl
    }
  }
`;

export type UspsCardProps = {
  data: FragmentType<typeof UspsCardFragmentFragmentDoc>;
};

export const UspsCard = ({ data }: UspsCardProps) => {
  const { infoList } = unmask<typeof UspsCardFragmentFragmentDoc>(data);

  const { formatMessage } = useIntl();

  if (infoList.length === 0) return null;

  const validInfoList = getValidInfoList(infoList, uspsMessages);

  if (validInfoList.length === 0) return null;

  return (
    <Card>
      <CardHeader hasDivider>
        {formatMessage({
          id: 'uspsCard.cardHeadline',
          defaultMessage: 'Deine Vorteile',
        })}
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-start gap-1 pb-2">
          {validInfoList.map((item) => (
            <AdditionalInfoItem
              key={item.translationKey}
              icon={
                item.iconUrl || <span className="icon-emp-checkmark text-color-text-dark size-4" />
              }
              serviceLayerId={item.serviceLayerId}
              text={formatMessage(item.messageDescriptor, {
                bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
              })}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
